/**
 * @file usePublishFlow.ts
 * @description Состояние окна публикации (phase19.16 Р1, Р1-6а; uiux-design-r1
 * §6, uiux-builder-shell.md §3.1; SB50, SB51, SB59, SB60, SB74, SB125).
 *
 * При открытии перечитывает «что изменено» и запоминает отпечаток черновика
 * — его отправляет публикация (сервер опубликует ровно то, что видел
 * человек, иначе 409 «Черновик изменился»). Р3а (SB102, SB126): ниже вида
 * сайта — страницы и места с правками (`usePagesPublish`); публикуется
 * отмеченное — сначала вид, затем страницы одной пачкой; частичный сбой
 * объясняется одной фразой.
 *
 * phase19.16 Р2а: «Вернуть элемент» у проверки шаблона страницы товара —
 * обязательный элемент возвращается в черновик шаблона, проверки
 * перечитываются, открытый шаблон в конструкторе — тоже.
 *
 * SB125: при ошибках проверок — галочка «публикую с незакрытыми проверками»
 * (право `SHOP_MANAGE_SETTINGS`). Она открывает кнопку и уходит на сервер
 * признаком `acknowledgeChecks`; сбрасывается при открытии и закрытии окна и
 * после каждой публикации — подтверждение каждый раз заново (п. 3). Та же
 * галочка снимает ошибки стоп-листа страниц (SB127).
 */

import { useContext, useEffect, useState } from "react";

import { BuilderContext } from "../../site-builder/builder-context";
import { PRODUCT_ELEMENT_INFO, type ProductRequiredKind } from "@chaiberi/shared";
import { restoreErrorText, restoreRequiredElement } from "../../../lib/site-builder/template-restore";

import { useHasPermission } from "../../../auth/hooks";
import { useNotify } from "../../ui/NotifyProvider";
import { useSiteView } from "../site-view-context";
import { siteView } from "../../../lib/api-site-view";
import { publishOutcomeText } from "../../../lib/site-builder/publish-rules";
import { usePagesPublish } from "./usePagesPublish";
import {
  apiErrorCode,
  parseAcknowledgeForbidden,
  parsePublishBlocked,
  parsePublishForbidden,
  siteViewErrorText,
} from "../../../lib/site-view/errors";
import { useOpenSite } from "../../../hooks/site-access/useOpenSite";
import type { SiteViewChanges } from "../../../lib/site-view/types";

type Outcome = "ok" | "failed" | "skipped";

export function usePublishFlow() {
  const view = useSiteView();
  // «Открыть сайт» после публикации: при закрытом сайте — через пропуск сотрудника (SB138).
  const openSite = useOpenSite(view.siteUrl);
  const notify = useNotify();
  const builder = useContext(BuilderContext);
  const builderPages = builder?.pages ?? null;
  // SB125 п. 2: публиковать с незакрытыми проверками — право настроек магазина.
  const canAcknowledge = useHasPermission("SHOP_MANAGE_SETTINGS");
  const open = view.dialog === "publish";
  const pages = usePagesPublish(open, view.pageTarget, view.currentUserId);
  const [snapshot, setSnapshot] = useState<SiteViewChanges | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewChecked, setViewChecked] = useState(true);
  const [acknowledged, setAcknowledgedState] = useState(false);
  const [acknowledgeError, setAcknowledgeError] = useState<string | null>(null);

  function setAcknowledged(value: boolean) {
    setAcknowledgedState(value);
    setAcknowledgeError(null);
  }

  async function loadSnapshot() {
    setProblem(null);
    // Список проверок перечитывается — текст галочки может стать другим: подтверждать заново.
    setAcknowledged(false);
    try {
      setSnapshot(await siteView.changes());
    } catch (err) {
      setProblem(siteViewErrorText(err, "Не удалось проверить черновик. Повторите."));
    }
  }

  useEffect(() => {
    // Галочка не переживает окно: и при открытии, и при закрытии — снята.
    setAcknowledged(false);
    if (!open) {
      setSnapshot(null);
      return;
    }
    void loadSnapshot();
    setViewChecked(true);
    // Пункты сбрасываются только при открытии окна.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const viewChanged = (snapshot?.changedParts.length ?? 0) > 0;
  const viewErrors = snapshot?.checks.filter((c) => c.level === "error") ?? [];
  // Ошибки не мешают, если владелец подтвердил публикацию с ними (SB125).
  const errorsAccepted = viewErrors.length === 0 || (acknowledged && canAcknowledge);
  const viewPublishable = !!snapshot && viewChanged && snapshot.blockedBy.length === 0 && errorsAccepted;
  // Пункт вида — с флажком, когда рядом есть страницы; иначе публикуется, если есть что.
  const withPages = pages.rows.length > 0;
  const doView = viewPublishable && (viewChecked || !withPages);
  // Ошибки стоп-листа отмеченных страниц ждут той же галочки (SB127, SB125).
  const pagesBlocked = pages.errors.length > 0 && !(acknowledged && canAcknowledge);
  const count = (doView ? 1 : 0) + pages.items.length;
  const canSubmit = count > 0 && !pagesBlocked;
  const submitLabel = count > 1 ? `Опубликовать ${count}` : "Опубликовать";

  async function publishView(): Promise<{ outcome: Outcome; acknowledged: number }> {
    if (!doView || !snapshot) return { outcome: "skipped", acknowledged: 0 };
    try {
      const res = await siteView.publish(snapshot.fingerprint, { acknowledgeChecks: acknowledged && viewErrors.length > 0 });
      return { outcome: "ok", acknowledged: res.acknowledged?.length ?? 0 };
    } catch (err) {
      const blocked = parsePublishForbidden(err);
      const checks = parsePublishBlocked(err);
      const ackForbidden = parseAcknowledgeForbidden(err);
      if (blocked) setSnapshot({ ...snapshot, canPublish: false, blockedBy: blocked });
      else if (checks) {
        // Сервер прислал свежие ошибки — галочка подтверждала прежний текст.
        setAcknowledged(false);
        setSnapshot({ ...snapshot, checks: [...checks, ...snapshot.checks.filter((c) => c.level !== "error")] });
      } else if (ackForbidden) setAcknowledgeError(ackForbidden);
      else if (apiErrorCode(err) === "site_view_draft_changed") {
        setProblem("Черновик изменился, пока вы смотрели. Проверьте изменения ещё раз.");
        await loadSnapshot();
      } else setProblem(siteViewErrorText(err, "Не удалось опубликовать — сайт остался прежним, черновик сохранён."));
      return { outcome: "failed", acknowledged: 0 };
    }
  }

  /** «Вернуть элемент» шаблона: в черновик, затем проверки заново. */
  async function restore(kind: ProductRequiredKind) {
    try {
      await restoreRequiredElement(kind);
      notify.success(`«${PRODUCT_ELEMENT_INFO[kind].label}» вернули в шаблон страницы товара`);
      if (builderPages?.page?.kind === "TEMPLATE") {
        // Блок шаблона изменён мимо истории — её записи устарели.
        builder?.undo.clear();
        await builderPages.reloadPage();
        builderPages.bumpCanvas();
      }
      await loadSnapshot();
    } catch (err) {
      notify.error(restoreErrorText(err));
    }
  }

  async function onPublish() {
    setBusy(true);
    const ack = acknowledged;
    try {
      const viewResult = await publishView();
      if (viewResult.outcome === "failed") return;
      const pagesResult = await pages.submit(ack && pages.errors.length > 0, setAcknowledgeError);
      // Подтверждение — на одну публикацию (SB125 п. 3).
      setAcknowledged(false);
      const skipped = viewResult.acknowledged + pagesResult.acknowledged;
      if (skipped > 0) notify.warning(`Опубликовано с незакрытыми проверками: ${skipped}`);
      const text = publishOutcomeText({ view: viewResult.outcome, pages: pagesResult.outcome, titles: pagesResult.titles });
      if (pagesResult.outcome === "failed") {
        // Окно остаётся открытым: причина и «Обновить» — в разделе страниц. Вид уже на
        // сайте — его снимок перечитываем, иначе повтор упрётся в «публиковать нечего».
        if (viewResult.outcome === "ok") {
          notify.warning(text);
          await loadSnapshot();
        }
      } else {
        view.openDialog(null);
        notify.success(text, {
          action: openSite ? { label: "Открыть сайт", onClick: () => openSite("/") } : undefined,
        });
      }
      if (viewResult.outcome === "ok") await view.reload();
    } finally {
      setBusy(false);
    }
  }

  return {
    open,
    pages,
    withPages,
    snapshot,
    problem,
    busy,
    viewChanged,
    viewErrors,
    viewPublishable,
    viewChecked,
    setViewChecked,
    acknowledged,
    setAcknowledged,
    canAcknowledge,
    acknowledgeError,
    canSubmit,
    submitLabel,
    onPublish,
    restore,
  };
}
